import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const API_BASE = process.env.REACT_APP_API_URL;

// Helper function to check and get valid token
const getValidToken = (getState) => {
  const state = getState();
  const token = state.user.token || sessionStorage.getItem("accessToken");
  
  if (!token) {
    throw new Error("No auth token");
  }
  
  return token;
};

// Helper function to handle auth errors and clear data
const handleAuthError = (error) => {
  if (error.message.includes("423")) {
    sessionStorage.removeItem("accessToken");
    return "423 - Account locked";
  }

  if (
    error.message.includes("401") ||
    error.message.includes("403") ||
    error.message === "No auth token"
  ) {
    sessionStorage.removeItem("accessToken");
    return "Authentication failed - please log in again";
  }

  return error.message;
};
// Create a new ticket
export const createTicket = createAsyncThunk(
  "tickets/createTicket",
  async (ticketData, { rejectWithValue, getState }) => {
    try {
      const token = getValidToken(getState);
      const state = getState();
      const user = state.user.user;

      const userFullName = user
        ? `${user.firstName} ${user.lastName}`
        : "Unknown User";

      const response = await fetch(`${API_BASE}/api/tickets/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(ticketData),
      });

      if (!response.ok) {
        const errorText = await response.json();//This line gets the response(error message in this case) from the API
        const errstring = errorText.messages[0];//this grabs its message part and makes it a string
        const clearerr = errstring.split(":").pop().trim();//gets rid of the header so it is just the info we want the user to see
        const errorMsg = clearerr || 'Failed to create ticket.';//what gets shown to the user, failed to create if nothing comes back otherwise the API error message
        throw new Error(errorMsg);
      }

      const created = await response.json();

      return {
        id: created.id,
        title: ticketData.title,
        status: ticketData.status ?? "UNASSIGNED",
        author: userFullName ?? "Unknown",
      };
    } catch (err) {
      return rejectWithValue(handleAuthError(err));
    }
  }
);
 
// Fetch all tickets
export const fetchAllTickets = createAsyncThunk(
  "tickets/fetchAllTickets",
  async (_, { rejectWithValue, getState }) => {
    try {
      const token = getValidToken(getState);

      const response = await fetch(`${API_BASE}/api/tickets`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tickets: ${response.status}`);
      }

      console.log("Yay tickets loaded!🎉");
      return await response.json();
    } catch (err) {
      return rejectWithValue(handleAuthError(err));
    }
  }
);
// Fetch ticket details by ID
export const fetchTicketById = createAsyncThunk(
  "tickets/fetchTicketById",
  async (ticketId, { rejectWithValue, getState }) => {
    try {
      const token = getValidToken(getState);
      if (!ticketId) throw new Error("No ticketId provided");

      const response = await fetch(`${API_BASE}/api/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch ticket: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      return rejectWithValue(handleAuthError(err));
    }
  }
);
// Update ticket
export const updateTicket = createAsyncThunk(
  "tickets/updateTicket",
  async ({ updates }, { rejectWithValue, getState }) => {
    try {
      const token = getValidToken(getState);
      const ticketId = getState().tickets.selectedTicket?.id;

      if (!ticketId) throw new Error("No selected ticket");

      const response = await fetch(
        `${API_BASE}/api/tickets/${ticketId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updates),
        }
      );

      if (!response.ok) {

        const errorData = await response.json();

        // Extract the messages array from ErrorResponse object and join them into a single string
        const errorMessage = errorData.messages && errorData.messages.length > 0 
          ? Array.from(errorData.messages).join(", ") 
          : "Failed to update ticket";

        throw new Error(errorMessage);
      }

      return {
        ...getState().tickets.selectedTicket,
        ...updates,
      };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

 
// Fetch ticket comments
export const fetchComments = createAsyncThunk(
  "tickets/fetchComments",
  async ({ ticketId, sortOrder = "asc" }, { rejectWithValue, getState }) => {
    try {
      const token = getValidToken(getState);
      const state = getState();

      const userId = state.user.user?.id;
      const response = await fetch(
        `${API_BASE}/api/tickets/${ticketId}/comments`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to load comments: ${response.status}`);
      }

      const data = await response.json();
 
      data.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
      });
 
      return data.map((c) => {
        const sentTime = (c.createdAt)
          ? new Date(`${c.createdAt.replace(" ", "T").replace("Z", "")}Z`).toLocaleString("en-US", {
              timeZone: 'America/Chicago',
              year: '2-digit', 
              month: 'numeric', 
              day: 'numeric', 
              hour: 'numeric', 
              minute: '2-digit', 
              second: '2-digit', 
              hour12: true 
            })
          : "";

        return {
          id: c.id,
          sender: c.authorName,
          message: c.comment,
          sentTime: sentTime,
          type: c.userid === userId ? "outgoing" : "incoming",
          profilePictureURL: c.profilePictureURL || c.profilePictureUrl || null
        };
      });
    } catch (err) {
      return rejectWithValue(handleAuthError(err));
    }
  }
);
 
// Post a comment
export const postComment = createAsyncThunk(
  "tickets/postComment",
  async ({ ticketId, comment }, { rejectWithValue, getState }) => {
    try {
      const token = getValidToken(getState);
      const state = getState();

      const userID = state.user.user?.id;

      const response = await fetch(
        `${API_BASE}/api/tickets/${ticketId}/comments`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ comment }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to post comment: ${response.status}`);
      }

      const savedComment = await response.json();
      const sentTime = (savedComment.createdAt)
        ? new Date(`${savedComment.createdAt.replace(" ", "T").replace("Z", "")}Z`).toLocaleString("en-US", {
          timeZone: 'America/Chicago',
          year: '2-digit', // 2-digit year (26 for 2026)
          month: 'numeric', // month without leading zero (3 for March)
          day: 'numeric', // day without leading zero (3 for 3th)
          hour: 'numeric', // hour without leading zero (9 for 9 AM))
          minute: '2-digit', // minute with leading zero (09 for 9 minutes)
          second: '2-digit', // second with leading zero (03 for 3 seconds)
          hour12: true // 12-hour format with AM/PM (9:09:03 AM)
        })
        : "";

      return {
        id: savedComment.id,
        sender: savedComment.authorName,
        message: savedComment.comment,
        sentTime: sentTime,
        type: savedComment.userid === userID ? "outgoing" : "incoming",
      };

    } catch (err) {
      return rejectWithValue(handleAuthError(err));
    }
  }
);

export const assignTicketToMe = createAsyncThunk(
  "tickets/assignTicketToMe",
  async (ticketId, { rejectWithValue, getState, dispatch }) => { 
    try {
      const { token } = getState().user;

      const assignResponse = await fetch(`${API_BASE}/api/tickets/${ticketId}/assignee`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!assignResponse.ok) {
        const errorData = await assignResponse.json().catch(() => ({}));
        throw new Error(
          "Assignment Failed: " + (errorData.messages || "Failed to assign ticket")
        );
      }

      const updatedTicketResponse = await fetch(`${API_BASE}/api/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return await updatedTicketResponse.json(); 
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);
export const assignTicketToSupport = createAsyncThunk(
  "tickets/assignTicket",
  async ({ticketId, supportUserId}, { rejectWithValue, getState, dispatch }) => { 
    try {
      const { token } = getState().user;

      const assignResponse = await fetch(`${API_BASE}/api/admin/tickets/${ticketId}/assignee`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ supportUserId: supportUserId }),
      });

      if (!assignResponse.ok) {
        const errorData = await assignResponse.json().catch(() => ({}));
        throw new Error(
          "Assignment Failed: " + (errorData.messages || "Failed to assign ticket")
        );
      }

      const updatedTicketResponse = await fetch(`${API_BASE}/api/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return await updatedTicketResponse.json(); 
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const reopenTicket = createAsyncThunk(
  "tickets/reopenTicket",
  async(ticketId, { rejectWithValue, getState}) => {
    try {
      const { token } = getState().user;

      const reopenResponse = await fetch(`${API_BASE}/api/admin/tickets/${ticketId}/reopenTicket`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if(!reopenResponse.ok) {
        const errorData = await reopenResponse.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to re-open ticket");
      }
      
      const data = await reopenResponse.json();
      return data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);
 
// Initial state
const initialTicketState = {
  id: null,
  title: "",
  description: "",
  status: "",
  author: "",
  createdAt: "",
  assigneeName: "",
};
 
const ticketSlice = createSlice({
  name: "tickets",
  initialState: {
    selectedTicket: initialTicketState,
    ticketsList: [],
    ticketStatus: "idle",
    comments: [],
    commentsStatus: "idle",
    error: null,
    author: "",
  },
  reducers: {
    setSelectedTicket(state, action) {
      state.selectedTicket = action.payload;
    },
    // Add action to clear all ticket data
    clearTicketData(state) {
      state.selectedTicket = initialTicketState;
      state.ticketsList = [];
      state.comments = [];
      state.error = null;
      state.ticketStatus = "idle";
      state.commentsStatus = "idle";
    },
    clearComment(state) {
      state.comments = [];
    }
  },
  extraReducers: (builder) => {
    builder
 
      // CREATE TICKET
      .addCase(createTicket.pending, (state) => {
        state.ticketStatus = "loading";
        state.error = null;
      })
      .addCase(createTicket.fulfilled, (state, action) => {
        state.ticketStatus = "succeeded";
        state.ticketsList.push(action.payload);  // add normally
        state.ticketsList.sort((a, b) => a.id - b.id); // sort by id
      })
      .addCase(createTicket.rejected, (state, action) => {
        state.ticketStatus = "failed";
        state.error = action.payload ?? action.error?.message;
        // Clear data if auth error
        if (action.payload?.includes("Authentication failed")) {
          state.ticketsList = [];
          state.selectedTicket = initialTicketState;
        }
      })
 
      // ALL TICKETS
      .addCase(fetchAllTickets.pending, (state) => {
        state.ticketStatus = "loading";
        state.error = null;
      })
      .addCase(fetchAllTickets.fulfilled, (state, action) => {
        state.ticketStatus = "succeeded";
        // Controller returns GetAllTicketsResponse(tickets)
        state.ticketsList = action.payload?.tickets;
      })
      .addCase(fetchAllTickets.rejected, (state, action) => {
        state.ticketStatus = "failed";
        state.error = action.payload ?? action.error?.message;
        // Always clear tickets on fetch failure
        state.ticketsList = [];
        // Clear selected ticket if auth error
        if (action.payload?.includes("Authentication failed")) {
          state.selectedTicket = initialTicketState;
        }
      })
 
      // SINGLE TICKET DETAILS
      .addCase(fetchTicketById.pending, (state) => {
        // state.ticketStatus = "loading";
        state.error = null;
      })
      .addCase(fetchTicketById.fulfilled, (state, action) => {
        // state.ticketStatus = "succeeded";
        state.selectedTicket = action.payload;
      })
      .addCase(fetchTicketById.rejected, (state, action) => {
        // state.ticketStatus = "failed";
        state.error = action.payload ?? action.error?.message;
        // Always clear selected ticket on fetch failure
        state.selectedTicket = initialTicketState;
      })

      // UPDATE TICKET
      .addCase(updateTicket.fulfilled, (state, action) => {
        const updatedTicket = action.payload;

        // Update selectedTicket if needed
        if (state.selectedTicket?.id === updatedTicket.id) {
          state.selectedTicket = { ...updatedTicket };
        }

        // Replace ticket in list
        const index = state.ticketsList.findIndex(t => t.id === updatedTicket.id);
        if (index !== -1) {
          state.ticketsList[index] = { ...updatedTicket };
        }
      })
      .addCase(updateTicket.rejected, (state, action) => {
        state.error = action.payload ?? action.error?.message;
        // Clear data if auth error
        if (action.payload?.includes("Authentication failed")) {
          state.ticketsList = [];
          state.selectedTicket = initialTicketState;
        }
      })
      .addCase(updateTicket.pending, (state) => {
        state.error = null;
      })
      // COMMENTS
      .addCase(fetchComments.pending, (state) => {
        state.commentsStatus = "loading";
        state.error = null;
      })
      .addCase(fetchComments.fulfilled, (state, action) => {
        state.commentsStatus = "succeeded";
        state.comments = action.payload || [];
      })
      .addCase(fetchComments.rejected, (state, action) => {
        state.commentsStatus = "failed";
        state.error = action.payload ?? action.error?.message;
        // Always clear comments on fetch failure
        state.comments = [];
      })
      .addCase(postComment.fulfilled, (state, action) => {
        state.comments.push(action.payload);
      })
      .addCase(postComment.rejected, (state, action) => {
        state.error = action.payload ?? action.error?.message;
        // Clear comments if auth error
        if (action.payload?.includes("Authentication failed")) {
          state.comments = [];
        }
      })
      .addCase(assignTicketToMe.pending, (state, action) => {
      })
      .addCase(assignTicketToMe.fulfilled, (state, action) => {
         const updatedTicket = action.payload;
        
        // Update dashboard ticket list
        state.ticketsList = state.ticketsList.map((t) =>
          t.id === updatedTicket.id ? updatedTicket : t
        );

        // Update ticket detailed view selected ticket
        if (state.selectedTicket?.id === updatedTicket.id) {
          state.selectedTicket = updatedTicket;
        }
      })
      .addCase(assignTicketToSupport.pending, (state, action) => {
      })
      .addCase(assignTicketToSupport.fulfilled, (state, action) => {
         const updatedTicket = action.payload;
        
        // Update dashboard ticket list
        state.ticketsList = state.ticketsList.map((t) =>
          t.id === updatedTicket.id ? updatedTicket : t
        );

        // Update ticket detailed view selected ticket
        if (state.selectedTicket?.id === updatedTicket.id) {
          state.selectedTicket = updatedTicket;
        }
      })
      .addCase(reopenTicket.fulfilled, (state, action) => {
        const ticketId = action.meta.arg;
        if (state.selectedTicket && state.selectedTicket.id === ticketId) {
          state.selectedTicket.status = "IN_PROGRESS";
        }

        const listToUpdate = state.ticketsList || state.tickets;

        if (listToUpdate) {
          const index = listToUpdate.findIndex(t => t.id === ticketId);
          if(index !== -1) {
            listToUpdate[index].status = "IN_PROGRESS";
        }
        }
      });
  },
});
 
export const { setSelectedTicket, clearTicketData, clearComment } = ticketSlice.actions;
export default ticketSlice.reducer;
export { getValidToken, handleAuthError };
